import clsx from 'clsx';
import type React from 'react';
import { useState } from 'react';

interface StudioTabProps {
  // Full-colour studio icon (greyed via CSS when idle)
  iconSrc: string;
  label: string;
  isActive: boolean;
  // When false the studio is not installed; the tab is shown but muted and non-interactive
  isInstalled: boolean;
  // Per-studio accent classes from STUDIO_CONFIG
  textClass: string;
  borderClass: string;
  onClick: () => void;
}

export const StudioTab: React.FC<StudioTabProps> = ({
  iconSrc,
  label,
  isActive,
  isInstalled,
  textClass,
  borderClass,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Show the studio's accent (text + full-colour icon) when active or hovered on an installed tab.
  const showAccent = isInstalled && (isActive || isHovered);

  // The studio accent colour is reserved for installed tabs. Not-installed tabs
  // stay muted and only darken to gray-600 on hover (never show the accent).
  const getTextColorClass = (): string => {
    if (isInstalled) {
      return showAccent ? textClass : 'text-gray-400';
    }
    return isHovered ? 'text-gray-600' : 'text-gray-400';
  };

  // The icon is a full-colour image greyed via CSS. Accent tabs show it in colour;
  // not-installed tabs keep it greyscale and darken it on hover (raised opacity) to
  // mirror the gray-600 text hover.
  const getIconClass = (): string => {
    if (showAccent) {
      return '';
    }
    if (!isInstalled && isHovered) {
      return 'grayscale opacity-90';
    }
    return 'grayscale opacity-40';
  };

  return (
    <button
      type='button'
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center h-full gap-1.5 md:gap-2 px-2 md:px-3 rounded-lg border border-transparent transition-colors min-w-0 shrink',
        'focus:outline-none focus-visible:outline-none cursor-pointer',
        isActive && clsx('bg-white shadow-sm font-semibold', borderClass),
        getTextColorClass(),
      )}>
      <img
        src={iconSrc}
        alt=''
        aria-hidden='true'
        className={clsx(
          'w-[16px] h-[16px] md:w-[18px] md:h-[18px] object-contain flex-shrink-0 transition-opacity',
          getIconClass(),
        )}
      />
      <span className='hidden sm:inline text-tiny md:text-xs leading-none truncate min-w-0'>
        {label}
      </span>
    </button>
  );
};

export default StudioTab;
